const DB_NAME = "mmj-library-downloads";
const DB_VERSION = 1;
const STORE_NAME = "downloads";
const MANIFEST_KEY = "mmj-library-offline-manifest";

const objectUrlCache = new Map();

function supportsIndexedDB() {
  return typeof indexedDB !== "undefined";
}

function openDatabase() {
  if (!supportsIndexedDB()) {
    return Promise.reject(new Error("IndexedDB not supported"));
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "product_id" });
        store.createIndex("downloaded_at", "downloaded_at", { unique: false });
        store.createIndex("media_type", "media_type", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
  });
}

async function withStore(mode, executor) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    let result;

    Promise.resolve()
      .then(() => executor(store, tx))
      .then((value) => {
        result = value;
      })
      .catch((error) => {
        reject(error);
      });

    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error || new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction aborted"));
  });
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
  });
}

function buildDownloadKey(item) {
  return String(item?.id || "").trim();
}

function buildFileName(item) {
  const safeTitle = String(item?.title || "mmj-file")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 80) || "mmj-file";
  const mediaType = String(item?.media_type || "bin").toLowerCase();
  const extension = mediaType === "pdf" ? "pdf" : mediaType === "audio" ? "mp3" : mediaType === "video" ? "mp4" : "bin";
  return `${safeTitle}.${extension}`;
}

function normalizeRecord(record) {
  if (!record) return null;
  const { blob, ...rest } = record;
  return {
    ...rest,
    has_blob: Boolean(blob),
  };
}

function readManifestFromStorage() {
  try {
    const raw = localStorage.getItem(MANIFEST_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeManifestToStorage(manifest) {
  try {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(manifest));
  } catch (error) {}
}

export async function listLibraryDownloads() {
  try {
    return await withStore("readonly", async (store) => {
      const records = await requestToPromise(store.getAll());
      return records.map(normalizeRecord);
    });
  } catch (error) {
    return [];
  }
}

export async function getLibraryDownload(productId) {
  const key = String(productId || "").trim();
  if (!key) return null;
  try {
    return await withStore("readonly", async (store) => normalizeRecord(await requestToPromise(store.get(key))));
  } catch (error) {
    return null;
  }
}

export async function hasLibraryDownload(productId) {
  return Boolean(await getLibraryDownload(productId));
}

export async function removeLibraryDownload(productId) {
  const key = String(productId || "").trim();
  if (!key) return false;
  const cachedUrl = objectUrlCache.get(key);
  if (cachedUrl) {
    URL.revokeObjectURL(cachedUrl);
    objectUrlCache.delete(key);
  }
  try {
    await withStore("readwrite", async (store) => {
      await requestToPromise(store.delete(key));
    });
    return true;
  } catch (error) {
    return false;
  }
}

export async function saveLibraryDownload({ item, blob, sourceUrl = "", mimeType = "", bytes = 0 }) {
  const productId = buildDownloadKey(item);
  if (!productId || !blob) {
    throw new Error("Invalid library download payload");
  }

  const record = {
    product_id: productId,
    title: item?.title || "MMJ",
    author_name: item?.author_name || item?.author || "ARTICO",
    media_type: item?.media_type || "bin",
    category: item?.category || "",
    subcategory: item?.subcategory || "",
    thumbnail_path: item?.thumbnail_path || item?.cover_image_path || "",
    source_url: sourceUrl,
    file_name: buildFileName(item),
    mime_type: mimeType || blob.type || "application/octet-stream",
    bytes: bytes || blob.size || 0,
    downloaded_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    progress: item?.progress || {},
    blob,
  };

  await withStore("readwrite", async (store) => {
    await requestToPromise(store.put(record));
  });

  const existingUrl = objectUrlCache.get(productId);
  if (existingUrl) {
    URL.revokeObjectURL(existingUrl);
    objectUrlCache.delete(productId);
  }

  return normalizeRecord(record);
}

export async function downloadLibraryItem({ item, url, headers = {}, sourceUrl = "" }) {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  const blob = await response.blob();
  return saveLibraryDownload({
    item,
    blob,
    sourceUrl,
    mimeType: blob.type,
    bytes: blob.size,
  });
}

export async function getLibraryDownloadObjectUrl(productId) {
  const key = String(productId || "").trim();
  if (!key) return null;
  const cached = objectUrlCache.get(key);
  if (cached) return cached;
  const record = await getLibraryDownload(key);
  if (!record?.has_blob) return null;

  const fullRecord = await withStore("readonly", async (store) => requestToPromise(store.get(key)));
  if (!fullRecord?.blob) return null;

  const objectUrl = URL.createObjectURL(fullRecord.blob);
  objectUrlCache.set(key, objectUrl);
  return objectUrl;
}

export async function getLibraryDownloadBlob(productId) {
  const key = String(productId || "").trim();
  if (!key) return null;
  const fullRecord = await withStore("readonly", async (store) => requestToPromise(store.get(key)));
  return fullRecord?.blob || null;
}

export function getLibraryDownloadFileName(item) {
  return buildFileName(item);
}

export async function syncOfflineManifest(syncPayload = {}, fallbackUserId = null) {
  const manifest = {
    user_id: syncPayload.user_id || fallbackUserId || null,
    synced_at: syncPayload.synced_at || new Date().toISOString(),
    items: Array.isArray(syncPayload.items) ? syncPayload.items : [],
    authorized_product_ids: Array.isArray(syncPayload.items)
      ? syncPayload.items.map((item) => String(item?.id || "")).filter(Boolean)
      : [],
    offline_policy: syncPayload.offline_policy || {},
  };
  writeManifestToStorage(manifest);
  return manifest;
}

export function getOfflineManifest() {
  return readManifestFromStorage();
}

// Legacy browser blobs are deliberately removed: offline works are now allowed only in the native encrypted vault.
export async function clearLegacyLibraryDownloads() {
  objectUrlCache.forEach((url) => URL.revokeObjectURL(url));
  objectUrlCache.clear();
  if (!supportsIndexedDB()) return;
  await new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = request.onerror = request.onblocked = () => resolve();
  });
}

export function isOfflineDownloadAllowed(productId) {
  const manifest = readManifestFromStorage();
  if (!manifest) return true;
  if (!Array.isArray(manifest.authorized_product_ids) || manifest.authorized_product_ids.length === 0) return true;
  return manifest.authorized_product_ids.includes(String(productId || ""));
}
